import React, { createContext, useContext, useState, useEffect } from 'react';

/**
 * Theme Provider (SRS-WEB-069)
 * Provides theme selection (light/dark mode) and accessibility options
 */
const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  // Load theme from localStorage or default to light
  const [theme, setTheme] = useState(() => {
    const savedTheme = localStorage.getItem('rms-theme');
    return savedTheme || 'light';
  });

  const [fontSize, setFontSize] = useState(() => {
    const saved = localStorage.getItem('rms-font-size');
    return saved || 'medium';
  });

  const [highContrast, setHighContrast] = useState(() => {
    const saved = localStorage.getItem('rms-high-contrast');
    return saved === 'true';
  });

  // Apply theme to document root
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    document.documentElement.setAttribute('data-font-size', fontSize);
    document.documentElement.setAttribute('data-high-contrast', highContrast);
  }, [theme, fontSize, highContrast]);

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    localStorage.setItem('rms-theme', newTheme);
  };

  const setFontSizeValue = (size) => {
    setFontSize(size);
    localStorage.setItem('rms-font-size', size);
  };

  const toggleHighContrast = () => {
    const newValue = !highContrast;
    setHighContrast(newValue);
    localStorage.setItem('rms-high-contrast', newValue.toString());
  };

  const value = {
    theme,
    fontSize,
    highContrast,
    toggleTheme,
    setFontSize: setFontSizeValue,
    toggleHighContrast
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

